<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { mapState } from "vuex";
import { REPORT_MODULE } from "@/state/modules/report";
import { reportMethods } from "@/state/helpers";

const PURPLES = ["#7C4DFF", "#9C6AFF", "#5E2FE0", "#B388FF", "#651FFF", "#6200EA", "#3D5AFE", "#536DFE", "#C9B8FF"];

const DOC_TYPE_LABEL = {
  quotation: "Quotation", salesorder: "Sales Order", deliveryorder: "Delivery Order",
  salesinvoice: "Invoice", creditnote: "Credit Note", consignment: "Consignment",
  cashsale: "Cash Sale", deliveryreturn: "Delivery Return", debitnote: "Debit Note",
};

import { crmLookupService } from "@/helpers/services/crmLookup.service";

export default {
  page: { title: i18n.t("module.report") },
  components: { Layout, PageHeader },
  data() {
    const today = new Date();
    const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
    return {
      title: i18n.t("module.report"),
      tabIndex: 0,
      filter: {
        dateFrom: threeMonthsAgo.toISOString().substr(0, 10),
        dateTo: today.toISOString().substr(0, 10),
      },
      clientGroupMode: "area", // 'area' or 'group'
      wonAnalysis: null,
      lostAnalysis: null,
      loadingState: false,
      selectedAgentLocation: null, // location_code of agent currently drilled into
      agentStockSubTab: 0, // 0 = pivot, 1 = timeline
    };
  },
  computed: {
    ...mapState(REPORT_MODULE, [
      "salesSummary", "topDebtors", "monthlyTrend", "weeklyTrend",
      "salesByAgent", "salesByProduct", "salesByClientGroup",
      "stockBuffer", "stockMovement", "marketStock", "agentStock", "agentStockDetail",
      "recentSalesDocs",
    ]),
    selectedAgent() {
      return (this.agentStock || []).find((a) => a.location_code === this.selectedAgentLocation);
    },
    summaryRows() { return this.salesSummary?.summary || []; },
    grandTotal() { return this.salesSummary?.grand_total || 0; },
    grandCount() { return this.salesSummary?.grand_count || 0; },

    docTypeChartSeries() {
      return this.summaryRows.filter((r) => r.count > 0).map((r) => r.total);
    },
    docTypeChartLabels() {
      return this.summaryRows.filter((r) => r.count > 0).map((r) => DOC_TYPE_LABEL[r.doc_type] || r.doc_type);
    },
    docTypeChartOptions() {
      return {
        chart: { fontFamily: "inherit" },
        labels: this.docTypeChartLabels,
        colors: PURPLES,
        legend: { position: "bottom", fontSize: "12px" },
        plotOptions: { pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Grand Total", formatter: () => `RM ${this.fmtShort(this.grandTotal)}` } } } } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` } },
      };
    },

    monthlyChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        stroke: { curve: "smooth", width: 3 },
        colors: ["#7C4DFF"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 90, 100] } },
        xaxis: { categories: this.monthlyTrend.map((m) => m.month), labels: { style: { fontSize: "11px" } } },
        yaxis: { labels: { formatter: (v) => `RM ${(v / 1000).toFixed(0)}k`, style: { fontSize: "11px" } } },
        tooltip: { y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` } },
        grid: { borderColor: "#f1f3f5", strokeDashArray: 4 },
        dataLabels: { enabled: false },
      };
    },
    monthlyChartSeries() { return [{ name: "Total Sales", data: this.monthlyTrend.map((m) => m.total) }]; },

    weeklyChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
        colors: ["#7C4DFF"],
        xaxis: { categories: this.weeklyTrend.map((w) => w.week), labels: { style: { fontSize: "10px" }, rotate: -45 } },
        yaxis: { labels: { formatter: (v) => `RM ${(v / 1000).toFixed(0)}k` } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` } },
        grid: { borderColor: "#f1f3f5", strokeDashArray: 4 },
      };
    },
    weeklyChartSeries() { return [{ name: "Weekly Sales", data: this.weeklyTrend.map((w) => w.total) }]; },

    topDebtorsOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 6, horizontal: true, barHeight: "65%", distributed: true } },
        colors: PURPLES,
        legend: { show: false },
        xaxis: { labels: { formatter: (v) => `RM ${(v / 1000).toFixed(0)}k`, style: { fontSize: "11px" } } },
        yaxis: { labels: { style: { fontSize: "11px" } } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` } },
        grid: { borderColor: "#f1f3f5", strokeDashArray: 4 },
      };
    },
    topDebtorsSeries() {
      return [{ name: "Total Sales", data: this.topDebtors.map((d) => ({ x: d.company_name || d.debtor_code, y: d.total_sales })) }];
    },

    agentChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 6, columnWidth: "55%", distributed: true } },
        colors: PURPLES,
        legend: { show: false },
        xaxis: { categories: this.salesByAgent.map((a) => a.agent), labels: { style: { fontSize: "11px" } } },
        yaxis: { labels: { formatter: (v) => `RM ${(v / 1000).toFixed(0)}k` } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` } },
        grid: { borderColor: "#f1f3f5", strokeDashArray: 4 },
      };
    },
    agentChartSeries() { return [{ name: "Total Sales", data: this.salesByAgent.map((a) => a.total_sales) }]; },

    productChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 6, horizontal: true, barHeight: "65%", distributed: true } },
        colors: PURPLES,
        legend: { show: false },
        xaxis: { labels: { formatter: (v) => `RM ${(v / 1000).toFixed(0)}k` } },
        yaxis: { labels: { style: { fontSize: "10px" } } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` } },
        grid: { borderColor: "#f1f3f5", strokeDashArray: 4 },
      };
    },
    productChartSeries() {
      return [{ name: "Total Sales", data: this.salesByProduct.map((p) => ({ x: `${p.item_code} — ${p.description || ''}`.substr(0, 35), y: p.total })) }];
    },

    clientGroupRows() { return this.salesByClientGroup?.rows || []; },
    clientGroupMode_() { return this.salesByClientGroup?.group_by || 'area'; },
    clientGroupChartOptions() {
      return {
        chart: { fontFamily: "inherit" },
        labels: this.clientGroupRows.map((g) => g.key || g.area_code || g.group_name),
        colors: PURPLES,
        legend: { position: "bottom" },
        plotOptions: { pie: { donut: { size: "60%" } } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` } },
      };
    },
    clientGroupChartSeries() { return this.clientGroupRows.map((g) => g.total); },

    profitRows() { return this.wonAnalysis?.by_group || []; },
    profitTotals() { return this.wonAnalysis?.totals || {}; },
    profitChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit", stacked: false },
        colors: ["#7C4DFF", "#C9B8FF"],
        xaxis: { categories: this.profitRows.map((r) => r.group) },
        legend: { position: "top" },
        dataLabels: { enabled: false },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        tooltip: { y: { formatter: (v) => this.fmtRm(v) } },
      };
    },
    profitChartSeries() {
      return [
        { name: this.$t("report.analysis.profit"), data: this.profitRows.map((r) => r.profit) },
        { name: this.$t("report.analysis.cost"), data: this.profitRows.map((r) => r.cost) },
      ];
    },

    lostRows() { return this.lostAnalysis?.by_reason || []; },
    lostChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        labels: this.lostRows.map((r) => this.$t(`opportunity.lostReason_.${r.reason}`) || r.reason),
        colors: ["#EF4444", "#F59E0B", "#8B5CF6", "#06B6D4", "#8993A4"],
        legend: { position: "bottom" },
        plotOptions: { pie: { donut: { size: "62%" } } },
        dataLabels: { enabled: true, formatter: (v) => v.toFixed(0) + "%" },
      };
    },
    lostChartSeries() { return this.lostRows.map((r) => r.count); },

    stockMovementChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit", stacked: true },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        colors: ["#10B981", "#F59E0B", "#7C4DFF", "#3D5AFE"],
        legend: { position: "top", horizontalAlign: "right" },
        xaxis: { categories: (this.stockMovement?.by_month || []).map((m) => m.month) },
        yaxis: { title: { text: "Qty" } },
        dataLabels: { enabled: false },
        grid: { borderColor: "#f1f3f5", strokeDashArray: 4 },
      };
    },
    stockMovementChartSeries() {
      const months = this.stockMovement?.by_month || [];
      return [
        { name: "IN", data: months.map((m) => m.IN || 0) },
        { name: "OUT", data: months.map((m) => m.OUT || 0) },
        { name: "TRANSFER", data: months.map((m) => m.TRF || 0) },
        { name: "ADJUSTMENT", data: months.map((m) => m.ADJ || 0) },
      ];
    },
  },
  async created() { await this.refresh(); },
  methods: {
    ...reportMethods,
    async refresh() {
      this.loadingState = true;
      const f = this.filter;
      await Promise.all([
        this.getSalesSummary({ filter: f }),
        this.getTopDebtors({ filter: { ...f, limit: 10 } }),
        this.getMonthlyTrend({ filter: f }),
        this.getWeeklyTrend({ filter: f }),
        this.getSalesByAgent({ filter: f }),
        this.getSalesByProduct({ filter: { ...f, limit: 15 } }),
        this.getSalesByClientGroup({ filter: { ...f, groupBy: this.clientGroupMode } }),
        this.getStockBuffer({ filter: { limit: 30 } }),
        this.getStockMovement({ filter: f }),
        this.getMarketStock({}),
        this.getAgentStock({}),
        this.getRecentSalesDocs({ filter: { limit: 10 } }),
        this.loadDealAnalysis(f),
      ]);
      this.loadingState = false;
    },
    async loadDealAnalysis(f) {
      const params = { date_from: f.dateFrom, date_to: f.dateTo };
      const [won, lost] = await Promise.all([
        crmLookupService.wonAnalysis(params).catch(() => null),
        crmLookupService.lostAnalysis(params).catch(() => null),
      ]);
      this.wonAnalysis = won?.data || null;
      this.lostAnalysis = lost?.data || null;
    },
    docTypeLabel(code) { return DOC_TYPE_LABEL[code] || code; },
    fmtRm(v) {
      const n = Number(v) || 0;
      return n.toLocaleString(undefined, { style: "currency", currency: "MYR", minimumFractionDigits: 2 });
    },
    async openAgentStock(card) {
      this.selectedAgentLocation = card.location_code;
      this.agentStockSubTab = 0;
      await this.getAgentStockDetail({ locationCode: card.location_code });
    },
    closeAgentStock() {
      this.selectedAgentLocation = null;
    },
    fmtShort(v) {
      const n = Number(v) || 0;
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
      return n.toFixed(0);
    },
    fmtQty(v) {
      const n = Number(v) || 0;
      return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },
    stockStatusVariant(s) {
      return s === "out_of_stock" ? "danger" : s === "low" ? "warning" : "success";
    },
    stockStatusLabel(s) {
      return s === "out_of_stock" ? "OUT" : s === "low" ? "LOW" : "OK";
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <!-- Date filter bar -->
    <div class="filter-bar">
      <div class="filter-fields">
        <div class="filter-field">
          <label>{{ $t('report.filters.from') }}</label>
          <b-form-input type="date" size="sm" v-model="filter.dateFrom" />
        </div>
        <div class="filter-field">
          <label>{{ $t('report.filters.to') }}</label>
          <b-form-input type="date" size="sm" v-model="filter.dateTo" />
        </div>
      </div>
      <b-button variant="primary" size="sm" @click="refresh" :disabled="loadingState">
        <i class="mdi mdi-refresh mr-1"></i>{{ loadingState ? $t('report.filters.loading') : $t('report.filters.refresh') }}
      </b-button>
    </div>

    <!-- KPI cards -->
    <div class="kpi-row">
      <div class="kpi-card kpi-purple">
        <div class="kpi-icon"><i class="mdi mdi-cash-multiple"></i></div>
        <div>
          <div class="kpi-num">RM {{ fmtShort(grandTotal) }}</div>
          <div class="kpi-cap">{{ $t('report.kpi.totalSales') }}</div>
        </div>
      </div>
      <div class="kpi-card kpi-blue">
        <div class="kpi-icon"><i class="mdi mdi-file-document-multiple-outline"></i></div>
        <div>
          <div class="kpi-num">{{ grandCount }}</div>
          <div class="kpi-cap">{{ $t('report.kpi.totalTransactions') }}</div>
        </div>
      </div>
      <div class="kpi-card kpi-cyan">
        <div class="kpi-icon"><i class="mdi mdi-account-multiple-outline"></i></div>
        <div>
          <div class="kpi-num">{{ topDebtors.length }}</div>
          <div class="kpi-cap">{{ $t('report.kpi.activeDebtors') }}</div>
        </div>
      </div>
      <div class="kpi-card kpi-green">
        <div class="kpi-icon"><i class="mdi mdi-package-variant"></i></div>
        <div>
          <div class="kpi-num">{{ stockBuffer.length }}</div>
          <div class="kpi-cap">{{ $t('report.kpi.stockItemsTracked') }}</div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="card">
      <div class="card-body">
        <b-tabs v-model="tabIndex" vertical pills nav-wrapper-class="rd-tabs-nav" content-class="rd-tabs-content">
          <!-- Sales Overview -->
          <b-tab>
            <template #title><i class="mdi mdi-chart-arc mr-1"></i>{{ $t('report.tabs.salesOverview') }}</template>
            <div class="row">
              <div class="col-12 col-lg-5">
                <h6 class="card-title-purple"><i class="mdi mdi-chart-donut"></i>{{ $t('report.section.transactionTypeBreakdown') }}</h6>
                <apexchart v-if="docTypeChartSeries.length" type="donut" height="300" :options="docTypeChartOptions" :series="docTypeChartSeries" />
                <div v-else class="empty">{{ $t('report.empty.noDataInRange') }}</div>
              </div>
              <div class="col-12 col-lg-7">
                <h6 class="card-title-purple"><i class="mdi mdi-chart-areaspline"></i>{{ $t('report.section.monthlyTrend') }}</h6>
                <apexchart v-if="monthlyTrend.length" type="area" height="300" :options="monthlyChartOptions" :series="monthlyChartSeries" />
                <div v-else class="empty">{{ $t('report.empty.noData') }}</div>
              </div>
              <div class="col-12 mt-3">
                <h6 class="card-title-purple"><i class="mdi mdi-table"></i>{{ $t('report.section.transactionTypeDetail') }}</h6>
                <b-table :items="summaryRows" small responsive striped :fields="[
                  { key: 'doc_type', label: $t('report.table.type'), formatter: (v) => docTypeLabel(v) },
                  { key: 'count', label: $t('report.table.count') },
                  { key: 'total', label: $t('report.table.total'), formatter: (v) => fmtRm(v) },
                ]" />
              </div>
            </div>
          </b-tab>

          <!-- Weekly Report -->
          <b-tab>
            <template #title><i class="mdi mdi-calendar-week mr-1"></i>{{ $t('report.tabs.weeklyReport') }}</template>
            <h6 class="card-title-purple"><i class="mdi mdi-chart-bar"></i>{{ $t('report.section.weeklySales') }}</h6>
            <apexchart v-if="weeklyTrend.length" type="bar" height="320" :options="weeklyChartOptions" :series="weeklyChartSeries" />
            <div v-else class="empty">{{ $t('report.empty.noWeekly') }}</div>
            <b-table v-if="weeklyTrend.length" class="mt-3" :items="weeklyTrend" small responsive striped :fields="[
              { key: 'week', label: $t('report.table.week') },
              { key: 'count', label: $t('report.table.count') },
              { key: 'total', label: $t('report.table.total'), formatter: (v) => fmtRm(v) },
            ]" />
          </b-tab>

          <!-- Agent Sales Report -->
          <b-tab>
            <template #title><i class="mdi mdi-account-tie mr-1"></i>{{ $t('report.tabs.agentSales') }}</template>
            <h6 class="card-title-purple"><i class="mdi mdi-chart-bar"></i>{{ $t('report.section.salesByAgent') }}</h6>
            <apexchart v-if="salesByAgent.length" type="bar" height="320" :options="agentChartOptions" :series="agentChartSeries" />
            <div v-else class="empty">{{ $t('report.empty.noAgent') }}</div>
            <b-table v-if="salesByAgent.length" class="mt-3" :items="salesByAgent" small responsive striped :fields="[
              { key: 'agent', label: $t('report.table.agent') },
              { key: 'doc_count', label: $t('report.table.transactions') },
              { key: 'total_sales', label: $t('report.table.total'), formatter: (v) => fmtRm(v) },
            ]" />
          </b-tab>

          <!-- Product Sales -->
          <b-tab>
            <template #title><i class="mdi mdi-package-variant-closed mr-1"></i>{{ $t('report.tabs.productSales') }}</template>
            <h6 class="card-title-purple"><i class="mdi mdi-chart-bar"></i>{{ $t('report.section.topProducts') }}</h6>
            <apexchart v-if="salesByProduct.length" type="bar" height="380" :options="productChartOptions" :series="productChartSeries" />
            <div v-else class="empty">{{ $t('report.empty.noProduct') }}</div>
            <b-table v-if="salesByProduct.length" class="mt-3" :items="salesByProduct" small responsive striped :fields="[
              { key: 'item_code', label: $t('report.table.code') },
              { key: 'description', label: $t('report.table.description') },
              { key: 'qty', label: $t('report.table.qty'), formatter: (v) => fmtQty(v) },
              { key: 'total', label: $t('report.table.total'), formatter: (v) => fmtRm(v) },
            ]" />
          </b-tab>

          <!-- Client Group -->
          <b-tab>
            <template #title><i class="mdi mdi-account-group mr-1"></i>{{ $t('report.tabs.clientGroup') }}</template>
            <div class="cg-toggle">
              <span class="cg-toggle-label">{{ $t('report.groupBy.label') }}</span>
              <b-button-group size="sm">
                <b-button :variant="clientGroupMode === 'area' ? 'primary' : 'outline-primary'" @click="clientGroupMode = 'area'; refresh()">
                  <i class="mdi mdi-map-marker mr-1"></i>{{ $t('report.groupBy.areaCode') }}
                </b-button>
                <b-button :variant="clientGroupMode === 'group' ? 'primary' : 'outline-primary'" @click="clientGroupMode = 'group'; refresh()">
                  <i class="mdi mdi-account-multiple mr-1"></i>{{ $t('report.groupBy.customGroup') }}
                </b-button>
              </b-button-group>
              <router-link v-if="clientGroupMode === 'group'" :to="{ name: 'Client-group-listing' }" class="ml-2 small text-purple">
                <i class="mdi mdi-cog-outline"></i> {{ $t('report.groupBy.manageGroups') }}
              </router-link>
            </div>
            <div class="row">
              <div class="col-12 col-lg-6">
                <h6 class="card-title-purple">
                  <i class="mdi mdi-chart-donut"></i>
                  {{ clientGroupMode === 'area' ? $t('report.section.salesByArea') : $t('report.section.salesByClientGroup') }}
                </h6>
                <apexchart v-if="clientGroupChartSeries.length" type="donut" height="320" :options="clientGroupChartOptions" :series="clientGroupChartSeries" />
                <div v-else class="empty">{{ $t('report.empty.noData') }}</div>
              </div>
              <div class="col-12 col-lg-6">
                <h6 class="card-title-purple"><i class="mdi mdi-table"></i>{{ $t('report.section.detail') }}</h6>
                <b-table :items="clientGroupRows" small responsive striped :fields="[
                  { key: 'key', label: clientGroupMode === 'area' ? $t('report.table.area') : $t('report.table.group') },
                  { key: 'doc_count', label: $t('report.table.transactions') },
                  { key: 'total', label: $t('report.table.total'), formatter: (v) => fmtRm(v) },
                ]" />
              </div>
            </div>
          </b-tab>

          <!-- Won: real profit, by customer group -->
          <b-tab>
            <template #title><i class="mdi mdi-trophy-outline mr-1"></i>{{ $t('report.tabs.wonProfit') }}</template>

            <div class="row">
              <div class="col-6 col-md-3">
                <div class="an-kpi">
                  <div class="an-label">{{ $t('report.analysis.revenue') }}</div>
                  <div class="an-value">{{ fmtRm(profitTotals.revenue || 0) }}</div>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="an-kpi">
                  <div class="an-label">{{ $t('report.analysis.cost') }}</div>
                  <div class="an-value">{{ fmtRm(profitTotals.cost || 0) }}</div>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="an-kpi an-profit">
                  <div class="an-label">{{ $t('report.analysis.profit') }}</div>
                  <div class="an-value">{{ fmtRm(profitTotals.profit || 0) }}</div>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="an-kpi">
                  <div class="an-label">{{ $t('report.analysis.margin') }}</div>
                  <div class="an-value">{{ (profitTotals.margin || 0) }}%</div>
                </div>
              </div>
            </div>

            <!-- Say plainly when costs are missing rather than reporting the
                 revenue as if it were all profit. -->
            <div v-if="profitTotals.uncosted_lines" class="an-warn">
              <i class="mdi mdi-alert-outline mr-1"></i>
              {{ $t('report.analysis.uncostedWarning', [profitTotals.uncosted_lines]) }}
            </div>

            <div class="row mt-3">
              <div class="col-12 col-lg-7">
                <h6 class="card-title-purple"><i class="mdi mdi-chart-bar"></i>{{ $t('report.analysis.profitByGroup') }}</h6>
                <apexchart v-if="profitRows.length" type="bar" height="320" :options="profitChartOptions" :series="profitChartSeries" />
                <div v-else class="empty">{{ $t('report.empty.noData') }}</div>
              </div>
              <div class="col-12 col-lg-5">
                <h6 class="card-title-purple"><i class="mdi mdi-table"></i>{{ $t('report.section.detail') }}</h6>
                <b-table :items="profitRows" small responsive striped :fields="[
                  { key: 'group', label: $t('report.table.group') },
                  { key: 'count', label: $t('report.table.transactions') },
                  { key: 'revenue', label: $t('report.analysis.revenue'), formatter: (v) => fmtRm(v) },
                  { key: 'profit', label: $t('report.analysis.profit'), formatter: (v) => fmtRm(v) },
                  { key: 'margin', label: $t('report.analysis.margin'), formatter: (v) => v + '%' },
                ]" />
              </div>
            </div>
          </b-tab>

          <!-- Lost: why business went elsewhere -->
          <b-tab>
            <template #title><i class="mdi mdi-close-circle-outline mr-1"></i>{{ $t('report.tabs.lostReasons') }}</template>

            <div class="row">
              <div class="col-6 col-md-4">
                <div class="an-kpi an-lost">
                  <div class="an-label">{{ $t('report.analysis.lostCount') }}</div>
                  <div class="an-value">{{ lostAnalysis && lostAnalysis.total || 0 }}</div>
                </div>
              </div>
              <div class="col-6 col-md-4">
                <div class="an-kpi an-lost">
                  <div class="an-label">{{ $t('report.analysis.lostValue') }}</div>
                  <div class="an-value">{{ fmtRm((lostAnalysis && lostAnalysis.total_amount) || 0) }}</div>
                </div>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-12 col-lg-5">
                <h6 class="card-title-purple"><i class="mdi mdi-chart-donut"></i>{{ $t('report.analysis.byReason') }}</h6>
                <apexchart v-if="lostChartSeries.length" type="donut" height="320" :options="lostChartOptions" :series="lostChartSeries" />
                <div v-else class="empty">{{ $t('report.empty.noData') }}</div>
              </div>
              <div class="col-12 col-lg-7">
                <h6 class="card-title-purple"><i class="mdi mdi-table"></i>{{ $t('report.section.detail') }}</h6>
                <b-table :items="lostRows" small responsive striped :fields="[
                  { key: 'reason', label: $t('opportunity.lostReason'), formatter: (v) => $t(`opportunity.lostReason_.${v}`) || v },
                  { key: 'percentage', label: '%', formatter: (v) => v + '%' },
                  { key: 'count', label: $t('report.table.transactions') },
                  { key: 'amount', label: $t('report.table.total'), formatter: (v) => fmtRm(v) },
                  { key: 'quotations', label: $t('report.analysis.fromQuotations') },
                  { key: 'deals', label: $t('report.analysis.fromDeals') },
                ]" />
              </div>
            </div>
          </b-tab>

          <!-- Top Debtors -->
          <b-tab>
            <template #title><i class="mdi mdi-podium mr-1"></i>{{ $t('report.tabs.topDebtors') }}</template>
            <h6 class="card-title-purple"><i class="mdi mdi-chart-bar"></i>{{ $t('report.section.topDebtors') }}</h6>
            <apexchart v-if="topDebtors.length" type="bar" height="420" :options="topDebtorsOptions" :series="topDebtorsSeries" />
            <div v-else class="empty">{{ $t('report.empty.noDebtor') }}</div>
          </b-tab>

          <!-- Stock Buffer -->
          <b-tab>
            <template #title><i class="mdi mdi-package-variant mr-1"></i>{{ $t('report.tabs.stockBuffer') }}</template>
            <h6 class="card-title-purple"><i class="mdi mdi-alert-circle-outline"></i>{{ $t('report.section.itemsNeedingAttention') }}</h6>
            <b-table :items="stockBuffer" small responsive striped :fields="[
              { key: 'item_code', label: $t('report.table.code') },
              { key: 'description', label: $t('report.table.description') },
              { key: 'balance', label: $t('report.table.balance'), formatter: (v) => fmtQty(v) },
              { key: 'reorder_level', label: $t('report.table.reorder'), formatter: (v) => fmtQty(v) },
              { key: 'buffer', label: $t('report.table.buffer'), formatter: (v) => fmtQty(v) },
              { key: 'status', label: $t('report.table.status') },
            ]">
              <template #cell(status)="data">
                <b-badge :variant="stockStatusVariant(data.item.status)">{{ stockStatusLabel(data.item.status) }}</b-badge>
              </template>
            </b-table>
            <div v-if="!stockBuffer.length" class="empty">{{ $t('report.empty.noStock') }}</div>
          </b-tab>

          <!-- Stock Movement -->
          <b-tab>
            <template #title><i class="mdi mdi-swap-horizontal mr-1"></i>{{ $t('report.tabs.stockMovement') }}</template>
            <div class="kpi-row mb-3">
              <div class="mini-kpi kpi-green"><span class="mk-label">IN</span><span class="mk-val">{{ fmtQty(stockMovement?.summary?.IN) }}</span></div>
              <div class="mini-kpi kpi-orange"><span class="mk-label">OUT</span><span class="mk-val">{{ fmtQty(stockMovement?.summary?.OUT) }}</span></div>
              <div class="mini-kpi kpi-purple"><span class="mk-label">TRANSFER</span><span class="mk-val">{{ fmtQty(stockMovement?.summary?.TRF) }}</span></div>
              <div class="mini-kpi kpi-blue"><span class="mk-label">ADJ</span><span class="mk-val">{{ fmtQty(stockMovement?.summary?.ADJ) }}</span></div>
            </div>
            <h6 class="card-title-purple"><i class="mdi mdi-chart-bar-stacked"></i>{{ $t('report.section.byMonth') }}</h6>
            <apexchart v-if="stockMovement && stockMovement.by_month && stockMovement.by_month.length" type="bar" height="320" :options="stockMovementChartOptions" :series="stockMovementChartSeries" />
            <div v-else class="empty">{{ $t('report.empty.noMovements') }}</div>
          </b-tab>

          <!-- Market Stock -->
          <b-tab>
            <template #title><i class="mdi mdi-store-outline mr-1"></i>{{ $t('report.tabs.marketStock') }}</template>
            <h6 class="card-title-purple"><i class="mdi mdi-table"></i>{{ $t('report.section.outstandingConsignment') }}</h6>
            <b-table :items="marketStock" small responsive striped :fields="[
              { key: 'debtor_code', label: $t('report.table.code') },
              { key: 'company_name', label: $t('report.table.customer') },
              { key: 'delivered_qty', label: $t('report.table.delivered'), formatter: (v) => fmtQty(v) },
              { key: 'sold_qty', label: $t('report.table.sold'), formatter: (v) => fmtQty(v) },
              { key: 'outstanding_qty', label: $t('report.table.outstanding'), formatter: (v) => fmtQty(v) },
              { key: 'doc_count', label: $t('report.table.transactions') },
            ]" />
            <div v-if="!marketStock.length" class="empty">{{ $t('report.empty.noConsignment') }}</div>
          </b-tab>

          <!-- Agent Stock -->
          <b-tab>
            <template #title><i class="mdi mdi-account-arrow-right mr-1"></i>{{ $t('report.tabs.agentStock') }}</template>

            <!-- Card grid (overview) -->
            <div v-if="!selectedAgentLocation">
              <div v-if="!agentStock.length" class="empty">
                No agent has a location code yet. Set <code>location_code</code> in the user form.
              </div>
              <div v-else class="agent-stock-grid">
                <div
                  v-for="a in agentStock"
                  :key="a.location_code"
                  class="agent-stock-card"
                  @click="openAgentStock(a)"
                >
                  <div class="agent-stock-head">
                    <i class="mdi mdi-account-circle"></i>
                    <div class="agent-stock-name">
                      <strong>{{ a.agent_name }}</strong>
                      <span class="agent-stock-loc">{{ a.location_code }}</span>
                    </div>
                  </div>
                  <div class="agent-stock-stats">
                    <div class="stat">
                      <span class="label">Items</span>
                      <span class="value">{{ a.item_count }}</span>
                    </div>
                    <div class="stat">
                      <span class="label">Total Qty</span>
                      <span class="value">{{ fmtQty(a.total_qty) }}</span>
                    </div>
                    <div class="stat">
                      <span class="label">Value</span>
                      <span class="value">{{ fmtRm(a.total_value) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Drill-down (one agent) -->
            <div v-else>
              <div class="d-flex align-items-center mb-3">
                <b-button size="sm" variant="light" @click="closeAgentStock" class="mr-3">
                  <i class="mdi mdi-arrow-left"></i> Back
                </b-button>
                <h6 class="mb-0">
                  <i class="mdi mdi-account-circle mr-1"></i>
                  {{ selectedAgent && selectedAgent.agent_name }}
                  <small class="text-muted ml-2">{{ selectedAgentLocation }}</small>
                </h6>
              </div>
              <b-tabs v-model="agentStockSubTab" pills small>
                <b-tab>
                  <template #title><i class="mdi mdi-table mr-1"></i>Stock by Item</template>
                  <b-table
                    :items="(agentStockDetail && agentStockDetail.balances) || []"
                    small responsive striped
                    :fields="[
                      { key: 'item_code', label: 'Item Code' },
                      { key: 'uom', label: 'UoM' },
                      { key: 'balance_qty', label: 'Qty', formatter: (v) => fmtQty(v) },
                      { key: 'balance_cost', label: 'Unit Cost', formatter: (v) => fmtRm(v) },
                    ]"
                    empty-text="No stock at this location"
                    show-empty
                  />
                </b-tab>
                <b-tab>
                  <template #title><i class="mdi mdi-history mr-1"></i>Movement Timeline</template>
                  <b-table
                    :items="(agentStockDetail && agentStockDetail.movements) || []"
                    small responsive striped
                    :fields="[
                      { key: 'doc_date', label: 'Date' },
                      { key: 'op_type', label: 'Type' },
                      { key: 'doc_no', label: 'Doc No' },
                      { key: 'description', label: 'Description' },
                      { key: 'total_qty', label: 'Qty', formatter: (v) => fmtQty(v) },
                    ]"
                    empty-text="No movements"
                    show-empty
                  />
                </b-tab>
              </b-tabs>
            </div>
          </b-tab>

          <!-- Recent Transactions -->
          <b-tab>
            <template #title><i class="mdi mdi-history mr-1"></i>{{ $t('report.tabs.recentTransactions') }}</template>
            <b-table :items="recentSalesDocs" small responsive striped :fields="[
              { key: 'doc_type', label: $t('report.table.type'), formatter: (v) => docTypeLabel(v) },
              { key: 'doc_no', label: $t('report.table.no') },
              { key: 'doc_date', label: $t('report.table.date') },
              { key: 'debtor_name', label: $t('report.table.customer') },
              { key: 'total', label: $t('report.table.total'), formatter: (v) => fmtRm(v) },
            ]" />
            <div v-if="!recentSalesDocs.length" class="empty">{{ $t('report.empty.noRecent') }}</div>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.an-kpi {
  background: #fff;
  border: 1px solid #e9ecef;
  border-left: 3px solid #adb5bd;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
}
.an-label { font-size: 11px; color: #8993A4; text-transform: uppercase; letter-spacing: 0.5px; }
.an-value { font-size: 19px; font-weight: 700; color: #212529; }
.an-profit { border-left-color: #10B981; }
.an-profit .an-value { color: #10B981; }
.an-lost { border-left-color: #EF4444; }
.an-lost .an-value { color: #EF4444; }
.an-warn {
  background: rgba(245, 158, 11, 0.1);
  color: #b45309;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  margin-bottom: 6px;
}

.filter-bar {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 16px; background: #fff; border: 1px solid #e9ecef; border-radius: 12px;
  padding: 12px 16px; margin-bottom: 16px;
}
.filter-fields { display: flex; gap: 12px; }
.filter-field { display: flex; flex-direction: column; gap: 4px; }
.filter-field label { font-size: 11px; color: #8993A4; font-weight: 600; text-transform: uppercase; }

.kpi-row {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px; margin-bottom: 16px;
}
.kpi-card {
  background: #fff; border-radius: 12px; padding: 14px 16px;
  display: flex; align-items: center; gap: 12px;
  border: 1px solid #e9ecef; position: relative; overflow: hidden;
}
.kpi-card::before { content: ''; position: absolute; top: 0; left: 0; bottom: 0; width: 4px; }
.kpi-purple::before { background: #7C4DFF; }
.kpi-blue::before { background: #3D5AFE; }
.kpi-cyan::before { background: #00BCD4; }
.kpi-green::before { background: #10B981; }
.kpi-orange::before { background: #F59E0B; }
.kpi-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
.kpi-purple .kpi-icon { background: rgba(124, 77, 255, 0.12); color: #7C4DFF; }
.kpi-blue .kpi-icon { background: rgba(61, 90, 254, 0.12); color: #3D5AFE; }
.kpi-cyan .kpi-icon { background: rgba(0, 188, 212, 0.12); color: #00BCD4; }
.kpi-green .kpi-icon { background: rgba(16, 185, 129, 0.12); color: #10B981; }
.kpi-num { font-size: 20px; font-weight: 700; color: #212529; line-height: 1.1; }
.kpi-cap { font-size: 10px; color: #8993A4; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 600; }

.mini-kpi {
  flex: 1; background: #fff; border: 1px solid #e9ecef; border-radius: 10px;
  padding: 10px 14px; display: flex; flex-direction: column; gap: 2px;
  border-left-width: 4px;
}
.mini-kpi.kpi-green { border-left-color: #10B981; }
.mini-kpi.kpi-orange { border-left-color: #F59E0B; }
.mini-kpi.kpi-purple { border-left-color: #7C4DFF; }
.mini-kpi.kpi-blue { border-left-color: #3D5AFE; }
.mk-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; color: #8993A4; font-weight: 700; }
.mk-val { font-size: 18px; font-weight: 700; color: #212529; }

::v-deep .rd-tabs-nav {
  width: 220px; flex-shrink: 0; padding-right: 14px; border-right: 1px solid #e9ecef;
}
::v-deep .rd-tabs-nav .nav-link {
  border-radius: 8px; padding: 10px 14px; margin-bottom: 4px;
  color: #495057; font-weight: 500; text-align: left; font-size: 13px;
  display: flex; align-items: center;
}
::v-deep .rd-tabs-nav .nav-link i { font-size: 16px; }
::v-deep .rd-tabs-nav .nav-link.active {
  background: linear-gradient(135deg, #7C4DFF, #9C6AFF);
  color: #fff;
  box-shadow: 0 4px 10px rgba(124, 77, 255, 0.25);
}
::v-deep .rd-tabs-content { flex: 1; padding-left: 20px; min-width: 0; }

.card-title-purple {
  font-size: 12px; font-weight: 700; color: #7C4DFF;
  text-transform: uppercase; letter-spacing: 0.4px;
  margin-bottom: 12px; display: flex; align-items: center; gap: 6px;
}
.card-title-purple i { font-size: 16px; }

.empty {
  padding: 40px 20px; text-align: center; color: #8993A4;
  border: 2px dashed #e9ecef; border-radius: 10px;
}

.cg-toggle {
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 16px;
}
.cg-toggle-label {
  font-size: 12px; font-weight: 600; color: #495057; text-transform: uppercase; letter-spacing: 0.3px;
}
.text-purple { color: #7C4DFF; text-decoration: none; }
.text-purple:hover { text-decoration: underline; }

.info-banner {
  background: rgba(124, 77, 255, 0.06);
  border: 1px solid rgba(124, 77, 255, 0.2);
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  display: flex; align-items: center; gap: 8px;
  font-size: 12px; color: #5E2FE0;
}
.info-banner i { font-size: 16px; }
.info-banner.warn {
  background: rgba(245, 158, 11, 0.06);
  border-color: rgba(245, 158, 11, 0.3);
  color: #B45309;
}

@media (max-width: 768px) {
  ::v-deep .rd-tabs-nav { width: 100%; padding-right: 0; border-right: 0; border-bottom: 1px solid #e9ecef; padding-bottom: 12px; }
  ::v-deep .rd-tabs-content { padding-left: 0; padding-top: 16px; }
}

.agent-stock-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
}
.agent-stock-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.agent-stock-card:hover {
  border-color: rgba(124, 77, 255, 0.4);
  box-shadow: 0 6px 16px rgba(124, 77, 255, 0.12);
  transform: translateY(-1px);
}
.agent-stock-head { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.agent-stock-head i { font-size: 32px; color: #7C4DFF; }
.agent-stock-name { display: flex; flex-direction: column; }
.agent-stock-name strong { font-size: 14px; color: #212529; }
.agent-stock-loc {
  font-size: 11px; color: #8993A4;
  font-family: monospace;
  background: rgba(124, 77, 255, 0.08);
  padding: 1px 6px;
  border-radius: 4px;
  align-self: flex-start;
  margin-top: 2px;
}
.agent-stock-stats { display: flex; justify-content: space-between; gap: 8px; }
.agent-stock-stats .stat { display: flex; flex-direction: column; flex: 1; }
.agent-stock-stats .label { font-size: 10px; color: #8993A4; text-transform: uppercase; letter-spacing: 0.5px; }
.agent-stock-stats .value { font-size: 14px; font-weight: 600; color: #212529; }
</style>
